// Firebase Web SDK wiring + push helpers.
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import axios from 'axios';

const cfg = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const API = process.env.REACT_APP_BACKEND_URL + '/api';

let _app = null;
let _messaging = null;

export const fcmConfigured = !!(cfg.apiKey && cfg.projectId && cfg.appId && VAPID_KEY);

async function getMessagingInstance() {
  if (!fcmConfigured) return null;
  if (!_app) _app = getApps().length ? getApps()[0] : initializeApp(cfg);
  if (_messaging) return _messaging;
  try {
    if (!(await isSupported())) return null;
    _messaging = getMessaging(_app);
    return _messaging;
  } catch (e) { return null; }
}

/**
 * Enable push:
 *  - registers /firebase-messaging-sw.js
 *  - requests notification permission
 *  - retrieves FCM token
 *  - posts token to backend
 * Returns the token string on success.
 */
export async function enablePush(onForegroundMessage) {
  if (!fcmConfigured) throw new Error('Firebase not configured');
  if (typeof Notification === 'undefined') throw new Error('Notifications unsupported');

  const messaging = await getMessagingInstance();
  if (!messaging) throw new Error('Messaging not supported on this browser');

  // Ask permission
  let permission = Notification.permission;
  if (permission === 'default') permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Permission denied');

  // Register the SW from the site root
  const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: reg,
  });
  if (!token) throw new Error('No FCM token');

  // Tell backend
  await axios.post(`${API}/push/subscribe`, {
    token,
    platform: 'web',
    user_agent: navigator.userAgent,
  }, { withCredentials: true });

  // Foreground listener: FCM suppresses the native banner when the tab is
  // focused, so we proactively call the ServiceWorker's showNotification()
  // which pushes the message into the OS notification bar (desktop + Android)
  // exactly like a background push. The optional `onForegroundMessage`
  // callback still fires so callers can layer an in-app toast if they want.
  onMessage(messaging, (payload) => {
    const title = payload.notification?.title || payload.data?.title || 'Tirth ERP';
    const body = payload.notification?.body || payload.data?.body || '';
    const link = payload.data?.link || '/notifications';
    const tag = payload.data?.event || 'tirth-erp';
    // Real OS notification via the SW registration (works on desktop + Android)
    try {
      reg.showNotification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag,
        data: { link },
        vibrate: [100, 50, 100],
        requireInteraction: false,
      });
    } catch (err) {
      // Fallback: direct Notification constructor
      try { new Notification(title, { body, icon: '/favicon.ico', tag }); } catch {}
    }
    if (onForegroundMessage) {
      onForegroundMessage({ title, body, link });
    }
  });

  // The FCM token is a *device subscription identifier*, NOT an auth secret.
  // It identifies which browser/device the server should push to, but holds no
  // authority on its own — server-side authorisation runs on the httpOnly
  // session cookie. Storing it in localStorage is the canonical Firebase
  // pattern and is safe even if exposed (an attacker possessing only the
  // token cannot impersonate the user; the server simply unsubscribes them
  // when the next push fails). Do NOT store auth tokens / passwords here.
  try { localStorage.setItem('ti_push_token', token); } catch {}
  return token;
}

/** Remove the stored token (best-effort). */
export async function disablePush() {
  const token = localStorage.getItem('ti_push_token');
  if (!token) return;
  try {
    await axios.post(`${API}/push/unsubscribe`, { token }, { withCredentials: true });
    localStorage.removeItem('ti_push_token');
  } catch {}
}

/**
 * Force a fresh FCM token registration. Use this when tokens are stale —
 * e.g. they were issued against a previous Firebase project, or the user
 * cleared site data on one device. Drops the old token (DB + localStorage)
 * then re-runs the subscribe flow.
 */
export async function resubscribePush(onForegroundMessage) {
  await disablePush();
  return enablePush(onForegroundMessage);
}

export function currentPushPermission() {
  return typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
}

export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export function isStandalonePWA() {
  return window.matchMedia && window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}
